import axios from 'axios';
import { v4 as uuid } from 'uuid';

interface TimeoutRetryOptions {
  attempts?: number;
  timeoutMs?: number;
  retryDelayMs?: number;
}

export const withTimeoutAndRetry = async <T>(
  fn: (signal: AbortSignal) => Promise<T>,
  {
    attempts = 3,
    timeoutMs = 30000,
    retryDelayMs = 1000,
  }: TimeoutRetryOptions = {}
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
      return await fn(controller.signal);
    } catch (error) {
      lastError = error;

      if (!isRetryable(error)) throw error;

      if (attempt < attempts) {
        await delay(retryDelayMs * attempt); // 1s, 2s, 3s
      }
    } finally {
      clearTimeout(timer);
    }
  }

  throw lastError;
};

const isRetryable = (error: unknown): boolean => {
  if (axios.isCancel(error)) return true;

  if (axios.isAxiosError(error)) {
    if (!error.response) return true;
    return error.response.status >= 500;
  }

  return false;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface FileUploadData {
  base64: string;
}

export const handleAdd = (
  setRecord: (data: FileUploadData) => void,
  setUploading: (uploading: boolean) => void,
  { base64 }: FileUploadData
): void => {
  setUploading(false);
  setRecord({ base64 });
};

export const handleDelete = (
  setRecord: (data: Record<string, never>) => void
): void => {
  setRecord({});
};

interface RestResponse<T> {
  data: T;
}

interface S3FileData {
  s3File: {
    key: string;
  };
}

interface UploadData {
  fileName: string;
  uploadId: string;
}

interface RequestBody {
  parts?: unknown;
}

export const mockRestClient = {
  get: (): RestResponse<Record<string, never>> => ({
    data: {},
  }),
  post: (
    _url: string,
    body: RequestBody
  ): RestResponse<S3FileData | UploadData> => {
    if (body.parts) {
      return {
        data: {
          s3File: {
            key: uuid(),
          },
        },
      };
    }
    return {
      data: {
        fileName: uuid(),
        uploadId: uuid(),
      },
    };
  },
};
